import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Info } from "lucide-react";

import { clx } from "../../../utils/Helpers";
import UIButton from "../UIButton";
import UIIcon from "../UIIcon";
import "./UIModal.scss";

const CONFIRM_VARIANTS = {
  info: "primary",
  success: "success",
  destructive: "destructive",
};

const MODAL_DEFAULT_ICONS = {
  destructive: (
    <UIIcon
      name="alertCircle"
      width={36}
      height={36}
      color="currentColor"
      decorative
      strokeWidth={3.11}
    />
  ),
  info: (
    <UIIcon
      name="infoCircle"
      width={36}
      height={36}
      color="currentColor"
      decorative
    />
  ),
};

const UIModal = ({
  open,
  title,
  description,
  children,
  actions,
  onClose,
  onCancel,
  onConfirm,
  cancelLabel,
  confirmLabel,
  cancelButtonProps,
  confirmButtonProps,
  type,
  icon,
  maxWidth,
  fullWidth,
  showClose,
  PaperProps,
  "aria-labelledby": ariaLabelledBy,
  ...dialogProps
}) => {
  const normalizedType = typeof type === "string" ? type.toLowerCase() : type;
  const isTypedModal = Boolean(normalizedType);
  const shouldShowClose = showClose ?? !isTypedModal;
  const paperClassName = clx(
    isTypedModal && "ui-modal",
    isTypedModal && `ui-modal--${normalizedType}`,
    PaperProps?.className,
  );
  const resolvedIcon = icon || MODAL_DEFAULT_ICONS[normalizedType] || (
    <Info size={36} strokeWidth={2.11} />
  );
  const generatedActions =
    isTypedModal && (cancelLabel || confirmLabel) ? (
      <div className="ui-modal__actions">
        {cancelLabel ? (
          <UIButton
            variant="outline"
            size="medium"
            fullWidth
            onClick={onCancel || onClose}
            {...cancelButtonProps}
          >
            {cancelLabel}
          </UIButton>
        ) : null}
        {confirmLabel ? (
          <UIButton
            variant={CONFIRM_VARIANTS[normalizedType]}
            size="medium"
            fullWidth
            onClick={onConfirm}
            {...confirmButtonProps}
          >
            {confirmLabel}
          </UIButton>
        ) : null}
      </div>
    ) : null;
  const renderedActions = actions || generatedActions;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{ ...PaperProps, className: paperClassName }}
      {...dialogProps}
    >
      {!isTypedModal && title && (
        <DialogTitle sx={{ pr: shouldShowClose ? 6 : undefined }}>
          {title}
          {shouldShowClose && (
            <IconButton
              aria-label="Close"
              onClick={onClose}
              sx={{ position: "absolute", right: 12, top: 10 }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
      )}
      {isTypedModal ? (
        <DialogContent className="ui-modal__content">
          <div className="ui-modal__header">
            <div className="ui-modal__icon" aria-hidden="true">
              {resolvedIcon}
            </div>
            <div className="ui-modal__heading">
              {title ? (
                <Typography
                  component="h2"
                  id={ariaLabelledBy}
                  className="ui-modal__title"
                >
                  {title}
                </Typography>
              ) : null}
              {description ? (
                <Typography component="div" className="ui-modal__description">
                  {description}
                </Typography>
              ) : null}
            </div>
          </div>
          {children ? (
            <>
              <Divider className="ui-modal__divider" />
              <div className="ui-modal__body">{children}</div>
            </>
          ) : null}
          {renderedActions}
        </DialogContent>
      ) : (
        <>
          <DialogContent>{children}</DialogContent>
          {renderedActions && (
            <DialogActions className="ui-modal__actions-container">
              {renderedActions}
            </DialogActions>
          )}
        </>
      )}
    </Dialog>
  );
};

UIModal.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.node,
  description: PropTypes.node,
  children: PropTypes.node,
  actions: PropTypes.node,
  onClose: PropTypes.func,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  cancelLabel: PropTypes.node,
  confirmLabel: PropTypes.node,
  cancelButtonProps: PropTypes.object,
  confirmButtonProps: PropTypes.object,
  type: PropTypes.oneOf([
    "info",
    "success",
    "destructive",
    "Info",
    "Success",
    "Destructive",
  ]),
  icon: PropTypes.node,
  maxWidth: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", false]),
  fullWidth: PropTypes.bool,
  showClose: PropTypes.bool,
  PaperProps: PropTypes.object,
  "aria-labelledby": PropTypes.string,
};

UIModal.defaultProps = {
  title: null,
  description: null,
  children: null,
  actions: null,
  onClose: undefined,
  onCancel: undefined,
  onConfirm: undefined,
  cancelLabel: null,
  confirmLabel: null,
  cancelButtonProps: undefined,
  confirmButtonProps: undefined,
  type: undefined,
  icon: null,
  maxWidth: "sm",
  fullWidth: true,
  showClose: undefined,
  PaperProps: {},
};

export default UIModal;
